// deps
import { useCallback, useContext, useRef } from "react";

// internal-deps
import { GetGroupCartOrderResponse, GroupOrderError } from "@swiggy-private/cart-types";
import { captureError, useMountedRef } from "@swiggy-private/core-ui";

// components
import { GroupCartStatusPoller } from "@cart-client/components/group-cart-renderer/GroupCartStatusPoller";

// utils/helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { getGroupOrderErrorPayload } from "@cart-client/helpers/GroupCartOrder";
import { randomStringIdGenerator } from "@swiggy-private/core-ui";

// contexts
import { GroupOrderCartContext, GroupCartActionTypes } from "@swiggy-private/checkout-core";

// apis
import { fetchGroupCartOrderItems } from "./useFetchGroupOrderCart";

interface useGroupCartPollingReturnType {
    enablePolling: VoidFunction;
    disablePolling: VoidFunction;
}

const POLLING_DURATION = 900000; // 15mins
const POLLING_DELAY = 3000; // 3s

const useGroupCartPolling = (groupCartId: string): useGroupCartPollingReturnType => {
    const { setState } = useContext(GroupOrderCartContext);
    const poller = useRef<GroupCartStatusPoller | null>(null);
    const currentPollingRequestId = useRef<string | null>(null);
    const isMountedRef = useMountedRef();

    const initialCallRef = useRef(true);

    const updateSharedCart = useCallback(
        async (newState: GetGroupCartOrderResponse, errorPayload: GroupOrderError) => {
            try {
                if (!isMountedRef.current || isEmpty(poller.current)) {
                    return;
                }

                // Do not update the page state if the new set of data is errored/empty
                if (isEmpty(newState.data)) {
                    setState({
                        type: GroupCartActionTypes.UPDATE_GROUP_CART_STATE,
                        payload: {
                            error: errorPayload,
                        },
                    });
                    return;
                }
                setState({
                    type: GroupCartActionTypes.UPDATE_GROUP_CART_STATE,
                    payload: {
                        data: newState.data,
                        error: errorPayload,
                        isCartLocked: newState.data?.locked_status || false,
                    },
                });

                // Update the initial state reference
                initialCallRef.current = false;
            } catch (err) {
                captureError(err);
            }
        },
        [isMountedRef, setState],
    );

    const disablePolling = useCallback(() => {
        poller.current?.stop();
    }, []);

    const getSharedCartState = useCallback(async () => {
        try {
            // Disabling api call if the view is hidden , since state change would be un-necessary to user in that case
            const isCurrentViewHidden = document.visibilityState == "hidden" || document.hidden;

            if (isEmpty(groupCartId) || !isMountedRef.current || isCurrentViewHidden) return;

            // generate req id for each polling call
            const currentRequestId = randomStringIdGenerator();
            currentPollingRequestId.current = currentRequestId;

            const data = await fetchGroupCartOrderItems(groupCartId);

            // validating if polling needs to be reflected
            if (
                !isMountedRef.current ||
                currentPollingRequestId.current !== currentRequestId ||
                isEmpty(poller.current)
            ) {
                return;
            }

            if (data.isErr()) {
                throw data;
            }

            const groupCartOrderData = data.unwrap();
            const errorPayload = getGroupOrderErrorPayload(groupCartOrderData);

            await updateSharedCart(groupCartOrderData, errorPayload);
        } catch (err) {
            if (initialCallRef.current) {
                // Should treat initial failure as a hard failure and should disable polling
                setState({
                    type: GroupCartActionTypes.UPDATE_GROUP_CART_STATE,
                    payload: {
                        error: { error: true },
                    },
                });
                disablePolling();
            }
            /* no-op since polling failures do not need to reflect i.e., silent failures */
        }
    }, [disablePolling, groupCartId, isMountedRef, setState, updateSharedCart]);

    const enablePolling = useCallback(
        (resumeWithDelay?: boolean) => {
            currentPollingRequestId.current = null;
            poller.current = new GroupCartStatusPoller();
            poller.current.enableGroupCartPolling({
                duration: POLLING_DURATION,
                delay: POLLING_DELAY,
                getSharedCartState,
                resumeWithDelay,
            });
        },
        [getSharedCartState],
    );

    return {
        disablePolling,
        enablePolling,
    };
};

export default useGroupCartPolling;
